import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ViewChild,
  ViewContainerRef,
  Type,
  ComponentRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FooterCellRenderer,
  FooterCellRendererParams,
  BuiltInFooterRenderer,
} from '../../core/interfaces/footer.interface';

/**
 * Footer Cell Renderer Loader Component
 *
 * Dynamically loads and renders custom footer cell renderer components.
 * Supports both Angular component renderers and built-in footer formatters.
 *
 * @example
 * ```html
 * <app-footer-cell-renderer-loader
 *   [renderer]="'currency'"
 *   [params]="footerParams">
 * </app-footer-cell-renderer-loader>
 * ```
 */
@Component({
  selector: 'app-footer-cell-renderer-loader',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container #rendererContainer></ng-container>
    <ng-container *ngIf="!customRenderer">
      <span [class]="footerClass">{{ formattedValue }}</span>
    </ng-container>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
    }
  `],
})
export class FooterCellRendererLoaderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() renderer?: Type<FooterCellRenderer<any>> | BuiltInFooterRenderer;
  @Input() params!: FooterCellRendererParams<any>;

  @ViewChild('rendererContainer', { read: ViewContainerRef })
  container!: ViewContainerRef;

  customRenderer: ComponentRef<FooterCellRenderer<any>> | null = null;
  formattedValue: string = '';
  footerClass: string = 'font-semibold';

  ngOnInit(): void {
    // Extract footer class
    const footerConfig = this.params.colDef.footer;
    if (footerConfig?.footerClass) {
      if (Array.isArray(footerConfig.footerClass)) {
        this.footerClass = footerConfig.footerClass.join(' ');
      } else {
        this.footerClass = footerConfig.footerClass;
      }
    }

    if (!this.renderer) {
      // No renderer - show aggregated value
      this.formattedValue = this.formatValue(this.params.value);
      return;
    }

    if (typeof this.renderer === 'string') {
      // Built-in renderer
      this.formattedValue = this.applyBuiltInRenderer(
        this.renderer,
        this.params.value,
        this.params
      );
    }
    // Component renderer will be loaded in ngAfterViewInit via container
  }

  ngAfterViewInit(): void {
    if (this.renderer && typeof this.renderer !== 'string') {
      // Load custom component renderer
      this.loadCustomRenderer(this.renderer);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update custom renderer params when loader's params change
    if (changes['params']) {
      // Update custom renderer instance with new params
      if (!changes['params'].firstChange && this.customRenderer) {
        this.customRenderer.instance.params = this.params;
        this.customRenderer.changeDetectorRef.markForCheck();
        this.customRenderer.changeDetectorRef.detectChanges();
      }

      // Update built-in renderer formatted value
      if (!this.customRenderer && this.renderer) {
        if (typeof this.renderer === 'string') {
          this.formattedValue = this.applyBuiltInRenderer(
            this.renderer,
            this.params.value,
            this.params
          );
        } else {
          this.formattedValue = this.formatValue(this.params.value);
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.customRenderer) {
      this.customRenderer.destroy();
    }
  }

  /**
   * Dynamically loads a custom footer renderer component
   */
  private loadCustomRenderer(
    rendererType: Type<FooterCellRenderer<any>>
  ): void {
    if (!this.container) {
      console.error('Container not available for custom footer renderer');
      return;
    }

    this.container.clear();
    this.customRenderer = this.container.createComponent(rendererType);

    // Pass params to the custom renderer
    this.customRenderer.instance.params = this.params;

    // Trigger change detection
    this.customRenderer.changeDetectorRef.detectChanges();
  }

  /**
   * Applies built-in footer renderer formatting
   */
  private applyBuiltInRenderer(
    renderer: BuiltInFooterRenderer,
    value: any,
    params: FooterCellRendererParams<any>
  ): string {
    switch (renderer) {
      case 'text':
        // Check if custom formatter is provided
        if (params.params?.formatter && typeof params.params.formatter === 'function') {
          return params.params.formatter(value);
        }
        return this.formatValue(value);

      case 'number':
        return this.formatNumber(value);

      case 'currency':
        return this.formatCurrency(value, params);

      case 'percentage':
        return this.formatPercentage(value);

      case 'blank':
        return '';

      default:
        console.warn(`Unknown built-in footer renderer: ${renderer}`);
        return this.formatValue(value);
    }
  }

  /**
   * Format helpers for built-in renderers
   */
  private formatValue(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }

  private formatNumber(value: any): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return Number(value).toLocaleString('es-HN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  private formatCurrency(value: any, params: FooterCellRendererParams<any>): string {
    if (value === null || value === undefined || isNaN(value)) return '';

    const footerParams = params.colDef.footer?.footerCellRendererParams || {};
    const currency = footerParams['currency'] || '';
    const locale = footerParams['locale'] || 'es-HN';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private formatPercentage(value: any): string {
    if (value === null || value === undefined || isNaN(value)) return '';
    return `${Number(value).toFixed(2)}%`;
  }
}
